import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { WorkflowTabBar } from '@/components/common';
import usePageTitle from '@/components/hooks/usePageTitle';
import { formatEmbryoName } from '@/grid/value-formatters';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  applyImplantBillingDefault,
  dateFields,
  EmbryoAdditionalTab,
  EmbryoCalvesTab,
  type EmbryoEditTab,
  embryoEditTabs,
  EmbryoFreezingTab,
  EmbryoGeneralTab,
  EmbryoImplantTab,
  EmbryoMeritInfoTab,
  EmbryoNotesTab,
  EmbryoQCTab,
  EmbryoRecipStatusTab,
  outsideEmbryoEditTabs,
  resolveEmbryoFormData,
  useAccountDataMap,
  useEmbryoFormValidation,
} from '@/pages/embryo-app/components/EmbryoForm';
import {
  type EmbryoFormData,
  useEmbryoValidationSchema,
} from '@/pages/embryo-app/schema';
import { hasPermission, MODULES, useCurrentUser } from '@/services/auth/util';
import {
  EditEmbryoInput,
  EditOutsideEmbryoInput,
  embryoServiceApi,
  useEditEmbryos,
  useEditOutsideEmbryos,
} from '@/services/embryo-service';
import { useToast } from '@/services/toast';
import { toDateOrNull } from '@/utils';

import { EmbryoDetailsToolbar } from './components/EmbryoDetailsToolbar';

export const EmbryoDetailsPage = () => {
  const implantBillingDefault = useFeatureFlag(
    'Implant_Billing_Account_Default',
  );
  const [params, setParams] = useSearchParams();
  const id = params.get('id') as string;
  const isOutsideEmbryo = params.get('type') === 'outside';
  const tabParam = params.get('tab') as EmbryoEditTab | null;

  const { t } = useTranslation();
  const { setTitle } = usePageTitle();
  const toast = useToast();
  const { data: currentUser } = useCurrentUser();
  const hasManagerWrite = hasPermission(
    currentUser,
    MODULES.EMBRYO_MANAGER_WRITE,
  );
  const [activeTab, setActiveTab] = useState<EmbryoEditTab>(
    tabParam || 'general',
  );
  const [embryoName, setEmbryoName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentStatuses, setCurrentStatuses] = useState<number[]>([]);
  const embryoActualIdRef = useRef<string>(id);
  const originalStateCodeRef = useRef<number | undefined>(undefined);
  const originalOwnerIdRef = useRef<string | undefined>(undefined);
  const accountDataMap = useAccountDataMap();
  const { mutateAsync: editEmbryos, isPending: isSavingRegular } =
    useEditEmbryos();
  const { mutateAsync: editOutsideEmbryos, isPending: isSavingOutside } =
    useEditOutsideEmbryos();
  const isSaving = isSavingRegular || isSavingOutside;

  const schema = useEmbryoValidationSchema();

  const methods = useForm<EmbryoFormData>({
    resolver: (values, context, options) => {
      const sanitized = JSON.parse(
        JSON.stringify(values, (_, v) => (v === null ? undefined : v)),
      );
      if (isOutsideEmbryo) {
        delete sanitized.status;
      }
      return zodResolver(schema)(sanitized, context, options);
    },
    mode: 'onChange',
  });
  const { formState } = methods;
  const isDirty = formState.isDirty;
  const isInactive = (methods.watch as any)('stateCode') === 1;

  useEffect(() => {
    const fetchEmbryoData = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await embryoServiceApi.getEmbryoById(
          id,
          isOutsideEmbryo,
        );

        if (response) {
          embryoActualIdRef.current = response.id;
          originalStateCodeRef.current = response.stateCode;
          originalOwnerIdRef.current = response.ownerId;
          const resolvedStatus = response.status;

          const resolvedDates = dateFields.reduce(
            (acc, field) => ({
              ...acc,
              [field]: toDateOrNull((response as any)[field]),
            }),
            {} as Record<string, any>,
          );

          methods.reset({
            ...resolveEmbryoFormData(response),
            ...resolvedDates,
          } as EmbryoFormData);

          if (implantBillingDefault) {
            applyImplantBillingDefault(methods.setValue, response);
          }

          if (resolvedStatus != null) {
            setCurrentStatuses([resolvedStatus]);
          }

          const name = formatEmbryoName(response) || id;
          setEmbryoName(name);
          setTitle(`${name}`, `Embryo ${name} Details`);
        } else {
          setError(true);
          setTitle(`Embryo ${id}`, `Embryo ${id} Details`);
        }
      } catch (err) {
        console.error('Error fetching embryo data:', err);
        setError(true);
        setTitle(`Embryo ${id}`, `Embryo ${id} Details`);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEmbryoData();
    }

    return () => {
      setTitle('');
    };
  }, [id, isOutsideEmbryo, methods, setTitle, implantBillingDefault]);

  const handleTabChange = (newTab: EmbryoEditTab) => {
    setActiveTab(newTab);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newTab === 'general') {
          next.delete('tab');
        } else {
          next.set('tab', newTab);
        }
        return next;
      },
      { replace: true },
    );
  };

  const {
    isSaveDisabled: validationDisabled,
    normalizeForSubmit,
    transactionFieldsMissing,
    ownerOrSoldToMissing: _ownerOrSoldToMissing,
    ownerPastDueBlocked: _ownerPastDueBlocked,
    statusMessages,
  } = useEmbryoFormValidation({
    methods: methods as any,
    currentStatuses,
    isBulkMode: false,
    originalStateCode: originalStateCodeRef.current,
    isOutsideEmbryo,
    accountDataMap,
    originalOwnerId: originalOwnerIdRef.current,
  });

  const isSaveDisabled = !isDirty || isSaving || validationDisabled;

  const handleInvalidSubmit = useCallback(
    (errors: Record<string, any>) => {
      const firstMessage = Object.values(errors)
        .map((e: any) => e?.message)
        .find(Boolean);
      toast.show({
        severity: 'error',
        summary: t('common.validationError'),
        detail: firstMessage ?? t('embryo.pleaseFixErrors'),
        life: 5000,
      });
    },
    [toast, t],
  );

  const handleSave = useCallback(
    async (formData: EmbryoFormData) => {
      const statusComment = (formData as any).statusComment as
        | string
        | undefined;
      try {
        const { statusComment: _removed, ...formDataWithoutComment } =
          formData as any;

        const normalized = normalizeForSubmit(formData);
        const payload = { ...formDataWithoutComment, ...normalized };

        let results: any;
        if (isOutsideEmbryo) {
          const {
            storageLocationId,
            cane,
            tt,
            clinicRanchHaulIn,
            ...baseFields
          } = payload;
          const outsideInput: EditOutsideEmbryoInput = {
            outsideEntryId: embryoActualIdRef.current,
            ...baseFields,
            ...(storageLocationId !== undefined && {
              freezeStorageLocationId: storageLocationId,
            }),
            ...(cane !== undefined && { freezeCane: cane }),
            ...(tt !== undefined && { freezeTt: tt }),
            ...(clinicRanchHaulIn !== undefined && {
              transferClinicRanchHaulIn: clinicRanchHaulIn,
            }),
          };
          results = await editOutsideEmbryos([outsideInput]);
        } else {
          const editInput: EditEmbryoInput = {
            ...payload,
            embryoId: embryoActualIdRef.current,
          };
          results = await editEmbryos([editInput]);
        }

        if (results && typeof results === 'object' && 'errors' in results) {
          const errorsObj = (results as any).errors;
          const errorDetail = Array.isArray(errorsObj)
            ? errorsObj.map((e: any) => e?.message ?? String(e)).join('; ')
            : JSON.stringify(errorsObj);

          toast.show({
            severity: 'error',
            summary: t('embryo.errorSavingEmbryo'),
            detail: errorDetail || t('common.anErrorOccurred'),
            life: 8000,
          });
          return;
        }

        const failedResults = Array.isArray(results)
          ? results.filter((r: any) => r.success === false)
          : [];
        if (failedResults.length > 0) {
          const apiMessage = failedResults
            .map((r: any) => r.message)
            .filter(Boolean)
            .join('; ');
          toast.show({
            severity: 'error',
            summary: t('embryo.errorSavingEmbryo'),
            detail: apiMessage || t('common.anErrorOccurred'),
            life: 8000,
          });
          return;
        }

        if (statusComment?.trim()) {
          const stateCode = (formData as any).stateCode;
          const prefix =
            stateCode === 0 ? 'Status Activated: ' : 'Status Deactivated: ';
          const noteText = `${prefix}${statusComment.trim()}`;
          try {
            await embryoServiceApi.createAnnotation(
              embryoActualIdRef.current,
              noteText,
            );
          } catch (err) {
            console.error('Failed to create annotation:', err);
          }
        }

        toast.show({
          severity: 'success',
          summary: t('common.success'),
          detail: t('embryo.embryoUpdatedSuccessfully'),
        });

        methods.reset({ ...methods.getValues(), ...formData });
        if (formData.status != null) {
          setCurrentStatuses([formData.status]);
        }
        if ((formData as any).stateCode !== undefined) {
          originalStateCodeRef.current = (formData as any).stateCode;
        }
      } catch (err) {
        console.error('Error saving embryo:', err);
        const message =
          err instanceof Error ? err.message : t('common.anErrorOccurred');
        toast.show({
          severity: 'error',
          summary: t('embryo.errorSavingEmbryo'),
          detail: message,
          life: 8000,
        });
      }
    },
    [
      methods,
      editEmbryos,
      editOutsideEmbryos,
      isOutsideEmbryo,
      toast,
      setCurrentStatuses,
      t,
      normalizeForSubmit,
    ],
  );

  if (loading) {
    return (
      <div className="mx-auto w-full p-4">
        {t('embryo.loadingEmbryoDetails')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full p-4">
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">
          <h3 className="mb-2 text-lg font-semibold">
            {t('embryo.embryoNotFound')}
          </h3>
          <p>{t('embryo.embryoNotFoundDetail', { id })}</p>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="flex h-full flex-col">
        <EmbryoDetailsToolbar
          embryoName={embryoName}
          onSave={methods.handleSubmit(handleSave, handleInvalidSubmit)}
          isSaveDisabled={isSaveDisabled}
          isSaving={isSaving}
          isInactive={isInactive}
        />

        <div className="flex flex-col gap-2 bg-white p-2">
          <WorkflowTabBar
            tabs={isOutsideEmbryo ? outsideEmbryoEditTabs : embryoEditTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            className="w-full"
          />
        </div>

        <div className="flex-1 overflow-auto p-4">
          {statusMessages.map((msg, i) => (
            <Message
              key={`${msg.severity}-${i}`}
              severity={msg.severity}
              text={`${msg.summary}: ${msg.detail}`}
              className="mb-2 w-full justify-start"
            />
          ))}
          {transactionFieldsMissing && (
            <Message
              severity="warn"
              text={`${t('common.warning')}: ${t(
                'embryo.transactionFieldsRequired',
                {
                  defaultValue:
                    'Transaction Date and Transaction Comment are required when Sold To is filled.',
                },
              )}`}
              className="mb-2 w-full justify-start"
            />
          )}

          {activeTab === 'general' && (
            <EmbryoGeneralTab
              control={methods.control as any}
              watch={methods.watch as any}
              isBulkMode={false}
              hasManagerWrite={hasManagerWrite}
              columns={3}
              isOutsideEmbryo={isOutsideEmbryo}
            />
          )}
          {activeTab === 'implant' && (
            <EmbryoImplantTab
              control={methods.control as any}
              watch={methods.watch as any}
              isBulkMode={false}
              hasManagerWrite={hasManagerWrite}
              columns={3}
              isOutsideEmbryo={isOutsideEmbryo}
              outsideEmbryoId={
                isOutsideEmbryo ? embryoActualIdRef.current : undefined
              }
            />
          )}
          {activeTab === 'freezing' && (
            <EmbryoFreezingTab
              control={methods.control as any}
              watch={methods.watch as any}
              setValue={methods.setValue as any}
              isBulkMode={false}
              columns={3}
              isOutsideEmbryo={isOutsideEmbryo}
              outsideEmbryoId={
                isOutsideEmbryo ? embryoActualIdRef.current : undefined
              }
            />
          )}
          {!isOutsideEmbryo && activeTab === 'qc' && (
            <EmbryoQCTab
              control={methods.control as any}
              isBulkMode={false}
              columns={3}
            />
          )}
          {activeTab === 'additional' && (
            <EmbryoAdditionalTab
              control={methods.control as any}
              watch={methods.watch as any}
              setValue={methods.setValue}
              isBulkMode={false}
              hasManagerWrite={hasManagerWrite}
              columns={4}
              originalStateCode={originalStateCodeRef.current}
              isOutsideEmbryo={isOutsideEmbryo}
            />
          )}
          {!isOutsideEmbryo && activeTab === 'meritInfo' && (
            <EmbryoMeritInfoTab
              control={methods.control as any}
              isBulkMode={false}
              columns={3}
            />
          )}
          {!isOutsideEmbryo && activeTab === 'recipStatus' && (
            <EmbryoRecipStatusTab
              control={methods.control as any}
              watch={methods.watch as any}
              isBulkMode={false}
              hasManagerWrite={hasManagerWrite}
              columns={3}
            />
          )}
          {!isOutsideEmbryo && activeTab === 'calves' && (
            <EmbryoCalvesTab
              embryoIds={
                embryoActualIdRef.current
                  ? [embryoActualIdRef.current]
                  : undefined
              }
            />
          )}
          {!isOutsideEmbryo && activeTab === 'notes' && (
            <EmbryoNotesTab embryoId={embryoActualIdRef.current} />
          )}
        </div>
      </div>
      <ConfirmDialog />
    </FormProvider>
  );
};
